/**
 * GlobalInputTranslator — a single component mounted once at the app
 * root that gives EVERY text <input> and <textarea> on the site a
 * floating "🌐 Translate my input" chip. When the user has typed
 * something and clicks the chip, we call `/api/i18n/translate`
 * (Emergent LLM key) and replace the field value in-place.
 *
 * Design goals
 *  - Zero-touch: no form component needs to change to gain the feature.
 *  - Non-invasive: chip only appears when the focused field is a
 *    meaningful text control with typed content.
 *  - Universal: supports 60+ target languages (defaults to English so
 *    non-English-speaking users can share content with English-primary
 *    admins).
 *
 * Detection rule: `focusin` on any `input[type=text|search|url|email|
 * tel]`, `textarea`, or `[contenteditable=true]` with at least 4 chars.
 * The chip is positioned via `getBoundingClientRect()` and re-anchors
 * on `scroll` / `resize`. On `focusout` we hide it (unless the pointer
 * moved to the chip itself).
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Languages, Loader2, X, Check } from "lucide-react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

// 60 languages — matches i18n backend `_SUPPORTED_LANGS`.
const LANGS = [
  { code: "en", label: "English" },
  { code: "es", label: "Español" },
  { code: "hi", label: "हिन्दी" },
  { code: "ta", label: "தமிழ்" },
  { code: "ml", label: "മലയാളം" },
  { code: "te", label: "తెలుగు" },
  { code: "kn", label: "ಕನ್ನಡ" },
  { code: "mr", label: "मराठी" },
  { code: "bn", label: "বাংলা" },
  { code: "gu", label: "ગુજરાતી" },
  { code: "pa", label: "ਪੰਜਾਬੀ" },
  { code: "ur", label: "اردو" },
  { code: "ar", label: "العربية" },
  { code: "fr", label: "Français" },
  { code: "de", label: "Deutsch" },
  { code: "pt", label: "Português" },
  { code: "ru", label: "Русский" },
  { code: "zh", label: "中文" },
  { code: "ja", label: "日本語" },
  { code: "ko", label: "한국어" },
  { code: "it", label: "Italiano" },
  { code: "nl", label: "Nederlands" },
  { code: "tr", label: "Türkçe" },
  { code: "vi", label: "Tiếng Việt" },
  { code: "th", label: "ภาษาไทย" },
  { code: "id", label: "Bahasa Indonesia" },
  { code: "fa", label: "فارسی" },
  { code: "he", label: "עברית" },
  { code: "pl", label: "Polski" },
  { code: "sv", label: "Svenska" },
  { code: "no", label: "Norsk" },
  { code: "da", label: "Dansk" },
  { code: "fi", label: "Suomi" },
  { code: "el", label: "Ελληνικά" },
];

const TEXT_INPUT_TYPES = new Set(["text", "search", "url", "email", "tel"]);
const MIN_CHARS = 4;
// Only skip on true "identity"/opaque inputs — very short constraint (max
// under 4 chars, e.g. OTP boxes). We keep the chip live on names, emails,
// URLs, and short handles so users writing in their native script can
// still translate a first-name / place-name to English.
const SKIP_NAMES = /(^|[-_])(otp|code|pin|cvv|zip|zipcode|postcode|token|captcha)([-_]|$)/i;
// Never surface the chip on password fields (even if type="text" for a
// visibility toggle) — value is sensitive and users rarely need it.
const SENSITIVE_NAMES = /(pass|pwd|secret)/i;
const STORAGE_KEY = "sp:input-translator-target";

function isEligibleField(el) {
  if (!el) return false;
  const tag = el.tagName;
  if (el.disabled || el.readOnly) return false;
  if (el.getAttribute("data-no-translate") === "true") return false;
  if (el.closest?.("[data-no-translate=true]")) return false;
  if (tag === "TEXTAREA") return true;
  if (tag === "INPUT") {
    const type = (el.type || "text").toLowerCase();
    if (!TEXT_INPUT_TYPES.has(type)) return false;
    const key = `${el.name || ""} ${el.id || ""} ${el.autocomplete || ""}`;
    if (SENSITIVE_NAMES.test(key)) return false;
    if (SKIP_NAMES.test(key)) return false;
    return true;
  }
  if (tag && el.getAttribute("contenteditable") === "true") return true;
  return false;
}

function getFieldValue(el) {
  if (!el) return "";
  if (el.tagName === "INPUT" || el.tagName === "TEXTAREA") return el.value || "";
  if (el.getAttribute?.("contenteditable") === "true") return el.innerText || "";
  return "";
}

function setFieldValue(el, next) {
  if (!el) return;
  if (el.tagName === "INPUT" || el.tagName === "TEXTAREA") {
    // Use the native setter so React's synthetic input onChange fires.
    const proto = el.tagName === "TEXTAREA" ? HTMLTextAreaElement.prototype : HTMLInputElement.prototype;
    const setter = Object.getOwnPropertyDescriptor(proto, "value")?.set;
    if (setter) setter.call(el, next);
    else el.value = next;
    el.dispatchEvent(new Event("input", { bubbles: true }));
    el.dispatchEvent(new Event("change", { bubbles: true }));
  } else if (el.getAttribute?.("contenteditable") === "true") {
    el.innerText = next;
    el.dispatchEvent(new Event("input", { bubbles: true }));
  }
}

export default function GlobalInputTranslator() {
  const [target, setTarget] = useState(() => {
    try { return localStorage.getItem(STORAGE_KEY) || "en"; }
    catch { return "en"; }
  });
  const [field, setField] = useState(null);   // DOM element or null
  const [rect, setRect] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showLangs, setShowLangs] = useState(false);
  const [justTranslated, setJustTranslated] = useState(false);
  const chipRef = useRef(null);

  // Save target choice.
  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, target); } catch { /* noop */ }
  }, [target]);

  // Attach global focus listener.
  useEffect(() => {
    const onFocusIn = (e) => {
      const el = e.target;
      if (chipRef.current && chipRef.current.contains(el)) return;
      if (!isEligibleField(el)) { setField(null); return; }
      const v = (getFieldValue(el) || "").trim();
      if (v.length < MIN_CHARS) { setField(null); return; }
      setField(el);
      setRect(el.getBoundingClientRect());
      setJustTranslated(false);
    };
    const onFocusOut = (e) => {
      // Delay so a click on our chip doesn't lose the field mid-flight.
      setTimeout(() => {
        const active = document.activeElement;
        if (chipRef.current && chipRef.current.contains(active)) return;
        if (active && isEligibleField(active)) return;
        setField(null);
        setShowLangs(false);
      }, 120);
    };
    const onInput = (e) => {
      const el = e.target;
      if (!isEligibleField(el)) return;
      if (el !== document.activeElement) return;
      const v = (getFieldValue(el) || "").trim();
      if (v.length < MIN_CHARS) {
        if (field === el) { setField(null); setShowLangs(false); }
        return;
      }
      setField(el);
      setRect(el.getBoundingClientRect());
    };
    const reflow = () => { if (field) setRect(field.getBoundingClientRect()); };

    document.addEventListener("focusin", onFocusIn);
    document.addEventListener("focusout", onFocusOut);
    document.addEventListener("input", onInput);
    window.addEventListener("scroll", reflow, true);
    window.addEventListener("resize", reflow);
    return () => {
      document.removeEventListener("focusin", onFocusIn);
      document.removeEventListener("focusout", onFocusOut);
      document.removeEventListener("input", onInput);
      window.removeEventListener("scroll", reflow, true);
      window.removeEventListener("resize", reflow);
    };
  }, [field]);

  const currentLang = useMemo(() => LANGS.find((l) => l.code === target) || LANGS[0], [target]);

  const runTranslate = async () => {
    if (!field) return;
    const text = (getFieldValue(field) || "").trim();
    if (text.length < MIN_CHARS) return;
    setBusy(true);
    try {
      const res = await axios.post(`${API}/api/i18n/translate`, {
        text,
        target_lang: target,
        source_lang: "auto",
      });
      const translated = res?.data?.translated;
      if (typeof translated === "string" && translated) {
        setFieldValue(field, translated);
        setJustTranslated(true);
        // Refresh anchor after value change (may resize textarea).
        setTimeout(() => { if (field) setRect(field.getBoundingClientRect()); }, 50);
        setTimeout(() => setJustTranslated(false), 2200);
      }
    } catch {
      // Silent — the chip stays visible so the user can retry.
    } finally {
      setBusy(false);
    }
  };

  if (!field || !rect) return null;
  if (typeof document === "undefined") return null;

  // Anchor: top-right of the field, floating just above it (or below if
  // there isn't room above).
  const chipHeight = 30;
  const spaceAbove = rect.top;
  const showBelow = spaceAbove < chipHeight + 8;
  const top = showBelow ? rect.bottom + 4 : rect.top - chipHeight - 4;
  const right = Math.max(8, window.innerWidth - rect.right);

  const chip = (
    <div
      ref={chipRef}
      data-testid="global-input-translator-chip"
      style={{
        position: "fixed",
        top,
        right,
        zIndex: 2147483000,      // above modals; keep just below iframes.
      }}
    >
      <div className="relative">
        <button
          type="button"
          onMouseDown={(e) => e.preventDefault()} // don't blur the field
          onClick={runTranslate}
          disabled={busy}
          data-testid="input-translate-btn"
          className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium shadow-lg border transition"
          style={{
            background: justTranslated ? "#DCFCE7" : "#111827",
            color: justTranslated ? "#166534" : "#FDFBF7",
            borderColor: justTranslated ? "#86EFAC" : "rgba(255,255,255,0.15)",
          }}
          title="Translate this field's text"
        >
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> :
           justTranslated ? <Check className="w-3.5 h-3.5" /> :
           <Languages className="w-3.5 h-3.5" />}
          <span>
            {busy ? "Translating…" : justTranslated ? "Translated" : `Translate → ${currentLang.label}`}
          </span>
        </button>
        <button
          type="button"
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => setShowLangs((v) => !v)}
          data-testid="input-translate-pick-lang"
          className="ml-1 inline-flex items-center justify-center rounded-full w-6 h-6 text-xs shadow border"
          style={{
            background: "#F3F4F6",
            color: "#111827",
            borderColor: "rgba(0,0,0,0.08)",
          }}
          title="Change target language"
          aria-label="Change target language"
        >
          ▾
        </button>

        {showLangs && (
          <div
            className="absolute right-0 mt-1 rounded-lg shadow-2xl border bg-white"
            style={{
              width: 220,
              maxHeight: 260,
              overflowY: "auto",
              borderColor: "#E5E7EB",
              top: chipHeight + 4,
            }}
            data-testid="input-translate-lang-menu"
          >
            <div className="flex items-center justify-between px-3 py-2 border-b" style={{ borderColor: "#E5E7EB" }}>
              <span className="text-xs font-mono uppercase tracking-wider" style={{ color: "#6B7280" }}>
                Target language
              </span>
              <button onClick={() => setShowLangs(false)} aria-label="Close" className="opacity-60">
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
            <ul className="py-1">
              {LANGS.map((l) => {
                const sel = l.code === target;
                return (
                  <li key={l.code}>
                    <button
                      type="button"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => { setTarget(l.code); setShowLangs(false); }}
                      data-testid={`input-translate-lang-${l.code}`}
                      className="w-full text-left px-3 py-1.5 text-sm hover:bg-gray-50 flex items-center justify-between"
                      style={{ color: sel ? "#111827" : "#374151", background: sel ? "#F9FAFB" : "transparent" }}
                    >
                      <span>{l.label}</span>
                      {sel && <Check className="w-3.5 h-3.5" style={{ color: "#059669" }} />}
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        )}
      </div>
    </div>
  );

  return createPortal(chip, document.body);
}
